import BarChartIcon from '@mui/icons-material/BarChart';
import { FiltersContextProvider } from '@openx/components-filters/lib/context/FiltersContext';
import type { FiltersContextProviderProps } from '@openx/components-filters/lib/context/types';
import { Table, TableName } from '@openx/components-table/Table';
import type { TileViewLabel } from '@openx/components-table/TileViewGrid/TileViewHeadLabels/TileViewHeadLabels';
import { CriteriaDimension } from '@openx/types/tableCriteria';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirectDealsListFilters } from '../../types';
import { RequestDirectDealModal } from '../components/requestModal/RequestDirectDealModal';
import { useDirectDealsContext } from '../utils/context/DirectDealsContext';
import { DIRECT_DEALS_LIST_VIEW } from '../utils/context/types';
import { useDirectDealsListColumns } from '../utils/hooks/useDirectDealsListColumns';
import { useDirectDealsListRowActions } from '../utils/hooks/useDirectDealsListRowActions';
import { DirectDealsHeader } from './DirectDealsHeader';
import { DirectDealsTopBar } from './DirectDealsTopBar';
import { getFiltersConfig } from './filtersConfig';
import { DirectDealsPublisherTile } from './parts/DirectDealsPublisherTile';
import { DirectDealsPublisherTilesLoading } from './parts/DirectDealsPublisherTilesLoading';

export const DirectDealsList = () => {
  const { useDirectDealsList } = useDirectDealsContext();
  const { rowActions } = useDirectDealsListRowActions();
  const { t } = useTranslation();

  const { tableCriteria, handleCriteriaChange, directDealsItems, loading, error, viewType, handleChangeViewType } =
    useDirectDealsList();
  const { columns } = useDirectDealsListColumns({ searchPhrase: tableCriteria.filters?.phrase });

  if (error) throw new Error('Publisher Marketplace Error');

  const headerLabels: TileViewLabel[] = [
    {
      labelIconComponent: BarChartIcon,
      labelText: t('Monthly Data'),
    },
  ];

  const onFiltersChange = useCallback(
    (value: DirectDealsListFilters) => {
      handleCriteriaChange({
        dimension: CriteriaDimension.FILTERS,
        value,
      });
    },
    [handleCriteriaChange]
  );

  const filtersProviderConfig: FiltersContextProviderProps<DirectDealsListFilters>['config'] = {
    filtersConfig: getFiltersConfig(t),
    visibleFilters: ['formats', 'geo_region', 'country'],
    criteria: tableCriteria,
    disabledFilters: ['tab'],
    onFiltersChange,
  };

  return (
    <FiltersContextProvider config={filtersProviderConfig}>
      <DirectDealsHeader currentViewType={viewType} onViewTypeChange={handleChangeViewType} />
      <DirectDealsTopBar />
      <Table
        name={TableName.DIRECT_DEALS}
        columns={columns}
        criteria={tableCriteria}
        data={directDealsItems}
        loading={loading}
        tileViewEnabled={viewType === DIRECT_DEALS_LIST_VIEW.TILE}
        onCriteriaChange={handleCriteriaChange}
        TileViewComponent={DirectDealsPublisherTile}
        TileViewLoadingComponent={DirectDealsPublisherTilesLoading}
        tileViewHeaderLabels={headerLabels}
        customRowActions={rowActions}
      />
      <RequestDirectDealModal />
    </FiltersContextProvider>
  );
};
